import React from "react";

const G = "#0077B5", GRN = "#1D9E75", B = "#E1E9EE";
const FONT = "-apple-system,'Segoe UI',Helvetica,sans-serif";
const SERIF = "'Playfair Display',Georgia,serif";

/**
 * Override Confirmation Screen — Phase 1B
 *
 * Shows a week-by-week view of scheduled overrides vs Authority Presence.
 * Phase 1B uses "Normal schedule" as placeholder since AP engine doesn't exist yet.
 *
 * Props:
 *   overrides - array of { weekStart: Date|string, type: string, label: string }
 *   onConfirm - called when user confirms
 *   onEdit    - called when user wants to go back and edit
 *   onCancel  - called when user cancels entirely
 */
export default function OverrideConfirmation({ overrides = [], onConfirm, onEdit, onCancel }) {
  // Build a 4-week view starting from the current week
  const now = new Date();
  const monday = new Date(now);
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
  monday.setHours(0, 0, 0, 0);

  // Determine how many weeks to show (cover all overrides + 1 gap week)
  const latestOverride = overrides.reduce((max, o) => { const d = new Date(o.weekStart); return d > max ? d : max; }, new Date());
  const weeksNeeded = Math.max(4, Math.ceil((latestOverride - monday) / (7 * 24 * 60 * 60 * 1000)) + 2);

  const weeks = [];
  for (let i = 0; i < weeksNeeded; i++) {
    const weekStart = new Date(monday);
    weekStart.setDate(weekStart.getDate() + i * 7);
    const weekKey = weekStart.toISOString().slice(0, 10);

    const matchingOverrides = overrides.filter(o => {
      const oDate = new Date(o.weekStart);
      oDate.setHours(0, 0, 0, 0);
      const oMonday = new Date(oDate);
      oMonday.setDate(oMonday.getDate() - ((oMonday.getDay() + 6) % 7));
      return oMonday.toISOString().slice(0, 10) === weekKey;
    });

    weeks.push({ weekStart, weekKey, overrides: matchingOverrides });
  }

  const hasMultipleInSameWeek = weeks.some(w => w.overrides.length > 1);

  // Find first week where AP resumes after overrides
  const overrideWeekKeys = new Set(overrides.map(o => {
    const d = new Date(o.weekStart);
    d.setHours(0, 0, 0, 0);
    const m = new Date(d);
    m.setDate(m.getDate() - ((m.getDay() + 6) % 7));
    return m.toISOString().slice(0, 10);
  }));
  const resumeWeek = weeks.find(w => !overrideWeekKeys.has(w.weekKey) && weeks.indexOf(w) > 0);

  const formatWeek = (d) => {
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  return (
    <div style={{ background: "#FFFFFF", border: `1px solid ${B}`, borderRadius: 14, padding: "32px", maxWidth: 560 }}>
      <div style={{ fontFamily: SERIF, fontSize: 22, marginBottom: 8 }}>Campaign Schedule</div>
      <div style={{ fontSize: 14, color: "#666", fontFamily: FONT, marginBottom: 24, lineHeight: 1.6 }}>
        Authority Presence will pause during campaign weeks and automatically resume afterward.
      </div>

      {/* Week-by-week view */}
      <div style={{ marginBottom: 24 }}>
        {weeks.map((w, i) => {
          const isOverride = w.overrides.length > 0;
          return (
            <div key={w.weekKey} style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "14px 0", borderBottom: i < weeks.length - 1 ? `1px solid ${B}` : "none" }}>
              <div style={{ fontSize: 13, color: "#888", fontFamily: FONT, minWidth: 100, flexShrink: 0 }}>
                Week of {formatWeek(w.weekStart)}
              </div>
              <div style={{ flex: 1 }}>
                {isOverride ? (
                  w.overrides.map((o, j) => (
                    <div key={j} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: j < w.overrides.length - 1 ? 6 : 0 }}>
                      <span style={{ fontSize: 14, fontFamily: FONT }}>{o.isNew ? "✎" : "📌"}</span>
                      <span style={{ fontSize: 14, color: o.isNew ? "#333" : "#888", fontFamily: FONT, fontWeight: 500 }}>{o.label || o.type}{!o.isNew ? " (existing)" : ""}</span>
                    </div>
                  ))
                ) : (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 14, fontFamily: FONT }}>⭐</span>
                    <span style={{ fontSize: 14, color: GRN, fontFamily: FONT, fontWeight: 500 }}>Normal schedule</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Overlap warning */}
      {hasMultipleInSameWeek && (() => {
        const hasNewAndExisting = weeks.some(w => w.overrides.length > 1 && w.overrides.some(o => o.isNew) && w.overrides.some(o => !o.isNew));
        return (
          <div style={{ background: "#FEF3C7", border: "1px solid #FCD34D", borderRadius: 8, padding: "12px 16px", marginBottom: 20, fontSize: 13, color: "#92400E", fontFamily: FONT }}>
            {hasNewAndExisting ? (
              <>⚠ <strong>Existing campaign detected.</strong> This new campaign will replace the existing one for the overlapping week(s).</>
            ) : (
              <>⚠ One campaign per week is recommended.</>
            )}
          </div>
        );
      })()}

      {/* Resume info */}
      {resumeWeek && overrides.length > 0 && (
        <div style={{ fontSize: 13, color: "#666", fontFamily: FONT, marginBottom: 20 }}>
          Authority Presence resumes: <strong>Week of {formatWeek(resumeWeek.weekStart)}</strong>
        </div>
      )}

      {/* Buttons */}
      <div style={{ display: "flex", gap: 10 }}>
        <button onClick={onConfirm} style={{ background: GRN, color: "#fff", border: "none", borderRadius: 10, padding: "12px 28px", fontFamily: FONT, fontSize: 15, fontWeight: 600, cursor: "pointer" }}>
          Confirm
        </button>
        <button onClick={onEdit} style={{ background: "transparent", color: G, border: `1px solid ${G}`, borderRadius: 10, padding: "12px 22px", fontFamily: FONT, fontSize: 14, cursor: "pointer" }}>
          Edit
        </button>
        <button onClick={onCancel} style={{ background: "transparent", color: "#888", border: `1px solid ${B}`, borderRadius: 10, padding: "12px 22px", fontFamily: FONT, fontSize: 14, cursor: "pointer" }}>
          Cancel
        </button>
      </div>
    </div>
  );
}
